import { useTranslation } from 'react-i18next';
import { getPotential, hasTarget } from '../../services/overviewMetricsService';
import { formatDkkCompact } from '../../utils/currencyUtils';
import type { OverviewLocation } from '../../types/overviewTypes';
import { MoneyMetric } from './MoneyMetric';
import { SeatsContext } from './SeatsContext';
import styles from './LocationCardBody.module.css';

interface LocationCardBodyProps {
  location: OverviewLocation | undefined;
  year: number;
}

/**
 * Picks the card's body from what the location actually has.
 *
 * Four states, and the distinctions all matter. Nothing known about the location
 * is not the same statement as no plan for the year — the first is the app
 * admitting it could not find out, the second is a fact about the plan. Below
 * those, a plan with no target has real figures and only lacks something to
 * measure them against.
 */
export const LocationCardBody = ({ location, year }: LocationCardBodyProps) => {
  const { t, i18n } = useTranslation();
  const format = (value: number): string => formatDkkCompact(value, i18n.language);

  // Nothing came back for this location — the whole load failed, or it answered
  // without this one. Never rendered as "no plan": that is a claim about the
  // year, and a failed request has not earned the right to make it.
  if (!location) {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyMessage}>{t('overview-figures-unknown')}</p>
      </div>
    );
  }

  // No plan for the year. A target may still be set, which is the January case:
  // the ceiling is known and the plan against it is still empty.
  if (!location.hasPlan) {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyMessage}>{t('overview-no-plan', { year })}</p>
        {hasTarget(location.money) && (
          <p className={styles.emptyFigure}>
            {t('overview-target-figure', { value: format(location.money.target) })}
          </p>
        )}
      </div>
    );
  }

  // A plan, but no ceiling typed in. The track's whole meaning is distance from
  // a target, so drawing one here would be a chart of nothing: figures only.
  // Scratch locations land in this state too, which keeps them out of the
  // overview's claims without reviving the location flag dropped on 2026-08-06.
  const money = location.money;
  if (!hasTarget(money)) {
    return (
      <>
        <div className={styles.empty}>
          <p className={styles.emptyMessage}>{t('overview-no-target', { year })}</p>
          <p className={styles.emptyFigure}>
            {t('overview-potential-figure', { value: format(getPotential(money)) })}
          </p>
          <p className={styles.emptyFigure}>
            {t('overview-realised-figure', { value: format(money.realised) })}
          </p>
        </div>
        <SeatsContext seats={location.seats} />
      </>
    );
  }

  return (
    <>
      <MoneyMetric
        metric={money}
        completionDependentMoney={location.completionDependentMoney}
        lockedFromWeek={location.lockedFromWeek}
        lockedToWeek={location.lockedToWeek}
      />
      <SeatsContext seats={location.seats} />
    </>
  );
};
